import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Upload,
  Eraser,
  ImageUp,
  Eye,
  Wand2,
  ScanSearch,
  Palette,
  Square,
  RectangleHorizontal,
  Type,
  QrCode,
  FileType,
  Scaling,
  GaugeCircle,
  Lock,
  Scissors,
  RotateCcw,
  Download,
  ImageIcon,
  PenTool,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useImageEditor } from '../../hooks/useImageEditor';
import {
  ICON_QR_SIZE_MIN,
  getIconQrSizeMax,
  getWatermarkFontSizeMax,
} from '../../lib/image-editor/overlayLimits';
import { getCanvasLogicalContentSize } from '../../lib/image-editor/imagePipeline';
import { CropperPanel } from '../../components/image-editor/CropperPanel';
import { AdjustmentPanel } from '../../components/image-editor/AdjustmentPanel';
import { BorderPanel } from '../../components/image-editor/BorderPanel';
import { IconPanel } from '../../components/image-editor/IconPanel';
import { DoodlePanel } from '../../components/image-editor/DoodlePanel';
import { WatermarkPanel } from '../../components/image-editor/WatermarkPanel';
import { QRCodePanel } from '../../components/image-editor/QRCodePanel';
import { CanvasStage } from '../../components/image-editor/CanvasStage';

type FeaturePanelId =
  | 'crop'
  | 'removeBg'
  | 'upscale'
  | 'adjust'
  | 'border'
  | 'canvas'
  | 'icon'
  | 'doodle'
  | 'watermark'
  | 'qrcode'
  | 'format'
  | 'scale'
  | 'compress';

interface FeatureToggleButtonProps {
  readonly label: string;
  readonly icon: React.ReactNode;
  readonly isActive?: boolean;
  readonly isModified?: boolean;
  readonly onClick: () => void;
  readonly onReset?: () => void;
  /** 用於 tab 語意，與下方 tabpanel 對應 */
  readonly tabId?: FeaturePanelId;
  readonly isTabSelected?: boolean;
}

function FeatureToggleButton({
  label,
  icon,
  isActive = false,
  isModified = false,
  onClick,
  onReset,
  tabId,
  isTabSelected = false,
}: FeatureToggleButtonProps) {
  const baseClasses =
    'inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm transition-colors';
  const inactiveClasses =
    'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700';
  const activeClasses =
    'bg-primary text-white border-primary shadow-sm hover:bg-primary/90';

  return (
    <button
      type="button"
      role={tabId !== undefined ? 'tab' : undefined}
      aria-selected={tabId !== undefined ? isTabSelected : undefined}
      id={tabId !== undefined ? `tab-${tabId}` : undefined}
      onClick={onClick}
      className={`${baseClasses} ${
        isModified || isActive ? activeClasses : inactiveClasses
      }`}
    >
      {icon}
      <span>{label}</span>
      {isModified && onReset && (
        <span
          role="button"
          aria-label={`${label} 回復預設`}
          className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
          onClick={event => {
            event.stopPropagation();
            onReset();
          }}
        >
          <RotateCcw className="h-3 w-3" />
        </span>
      )}
    </button>
  );
}

/**
 * 圖片修改器：上傳圖片、裁剪、AI 去背／放大、輸出尺寸與格式，全部在前端完成。
 *
 * 主要邏輯集中在 useImageEditor hook 與 image editor 核心模組中，此元件只負責排版與操作分區。
 */
const THUMB_SIZE = 72;
const CAROUSEL_ITEM_GAP = 8;

export function ImageEditorTool() {
  const editor = useImageEditor();
  const carouselRef = useRef<HTMLDivElement>(null);

  /** 縮圖用 object URL，與 editor.files 同步，unmount 或 files 變更時 revoke */
  const [thumbnailUrls, setThumbnailUrls] = useState<string[]>([]);
  useEffect(() => {
    const files = editor.files;
    if (files.length === 0) {
      setThumbnailUrls([]);
      return;
    }
    const urls = files.map(f => URL.createObjectURL(f));
    setThumbnailUrls(urls);
    return () => {
      urls.forEach(u => URL.revokeObjectURL(u));
    };
  }, [editor.files]);

  const scrollCarousel = useCallback((direction: 'prev' | 'next') => {
    const el = carouselRef.current;
    if (!el) return;
    const step = THUMB_SIZE + CAROUSEL_ITEM_GAP;
    el.scrollBy({
      left: direction === 'prev' ? -step : step,
      behavior: 'smooth',
    });
  }, []);

  const [activeFeatureId, setActiveFeatureId] = useState<FeaturePanelId | null>(
    'crop'
  );

  /** 點擊功能按鈕時開啟的設定面板（桌機＝對話框、手機＝drawer） */
  /** 目前選中的功能 tab，null 時不顯示設定區 */
  const [featurePanelOpen, setFeaturePanelOpen] =
    useState<FeaturePanelId | null>('crop');

  /** 裁切面板：原圖大小（預設）或自訂大小 */
  const [cropSizeMode, setCropSizeMode] = useState<'sameAsImage' | 'custom'>(
    'sameAsImage'
  );

  /** 畫布尺寸：目前選中的長寬比（點哪個就變色），手動改寬高或原圖大小時清空 */
  const [canvasAspectRatioId, setCanvasAspectRatioId] = useState<string | null>(
    null
  );

  /** 點擊預覽中文字時，指定要展開的浮水印區塊並打開文字／浮水印面板 */
  const [watermarkExpandBlockId, setWatermarkExpandBlockId] = useState<
    string | null
  >(null);

  /** 輸出尺寸：用比例拉桿 或 手動輸入畫素，二選一 */
  const [scaleInputMode, setScaleInputMode] = useState<'ratio' | 'pixels'>(
    'ratio'
  );

  /** 手動輸入畫素時的寬／高字串，onBlur 才套用 */
  const [scalePixelWidth, setScalePixelWidth] = useState('');
  const [scalePixelHeight, setScalePixelHeight] = useState('');

  /** 手動輸入畫素時是否鎖定長寬比（鎖定時改寬或高會同步另一邊） */
  const [scalePixelLock, setScalePixelLock] = useState(true);

  const {
    acceptImage,
    file,
    originalSize,
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileChange,
    previewUrl,
    noBackgroundUrl,
    removeBackgroundLoading,
    handleRemoveBackground,
    handleClearNoBackground,
    handleUpscale,
    handleClearUpscale,
    upscaleLoading,
    upscaledUrl,
    outputWidth,
    outputHeight,
    committedOutputWidth,
    committedOutputHeight,
    effectiveOutputWidth,
    effectiveOutputHeight,
    commitOutputSize,
    setOutputWidth,
    setOutputHeight,
    aspectRatios,
    applyCanvasAspectRatio,
    minPx,
    maxPx,
    outputFormats,
    formatId,
    setFormatId,
    error,
    outputBackgroundType,
    outputBackgroundColor,
    outputBackgroundImage,
    setOutputBackgroundType,
    setOutputBackgroundColor,
    setOutputBackgroundImage,
    canvasAnchorX,
    canvasAnchorY,
    compressionRatio,
    outputScaleRatio,
    targetOutputPixels,
    estimatedOutputSize,
    canvasContentSize,
    minOutputCanvasSize,
    canvasSizeMode,
    setCanvasSizeMode,
    setCanvasAnchor,
    setCompressionRatio,
    setOutputScaleRatio,
    setTargetOutputPixels,
    clearTargetOutputPixels,
    brightness,
    contrast,
    saturation,
    borderShape,
    borderCornerRadius,
    borderWidth,
    handleResetBorder,
    handleResetWatermark,
    cropRect,
    aspectRatioId,
    useSmartCrop,
    isCroppingMode,
    handleToggleCropMode,
    handleResetCrop,
    processing,
    pipelineCurrentStep,
    pipelineCompletedSteps,
    handleOutput,
    handleNineGridSlice,
    gridSliceCount,
    setGridSliceCount,
    handleMergeImages,
    mergeAspectRatios,
    mergeAspectRatioId,
    setMergeAspectRatioId,
    mergeLayouts,
    mergeLayoutId,
    setMergeLayoutId,
  } = editor;

  const openFeaturePanel = (id: FeaturePanelId) => {
    setActiveFeatureId(id);
    setFeaturePanelOpen(id);
    if (id === 'crop' && !isCroppingMode) {
      handleToggleCropMode();
    }
  };

  /** 預覽中點擊浮水印文字：打開文字／浮水印面板並展開該區塊 */
  const handleWatermarkPreviewClick = (blockId: string) => {
    openFeaturePanel('watermark');
    setWatermarkExpandBlockId(blockId);
  };

  /** 切到自訂大小時自動進入裁切模式，確保 cropRect 存在以顯示寬高輸入 */
  const handleCropSizeModeChange = (mode: 'sameAsImage' | 'custom') => {
    setCropSizeMode(mode);
    if (mode === 'custom' && !isCroppingMode) {
      handleToggleCropMode();
    }
  };

  const effectivePreviewUrl = previewUrl;

  /** 裁切為「自訂大小」且有變更時才顯示回復鍵；選「原圖大小」視為未修改 */
  const isCropModified = Boolean(
    cropSizeMode === 'custom' &&
      ((cropRect &&
        (cropRect.x !== 0 ||
          cropRect.y !== 0 ||
          cropRect.width !== 1 ||
          cropRect.height !== 1)) ||
        aspectRatioId !== 'original' ||
        useSmartCrop)
  );

  const isAdjustModified =
    brightness !== 0 || contrast !== 0 || saturation !== 0;

  const isBorderModified =
    borderShape !== 'rect' || borderCornerRadius > 0 || borderWidth > 0;

  const isCanvasModified = Boolean(
    canvasSizeMode === 'custom' &&
      minOutputCanvasSize &&
      committedOutputWidth > 0 &&
      committedOutputHeight > 0 &&
      (committedOutputWidth !== minOutputCanvasSize.width ||
        committedOutputHeight !== minOutputCanvasSize.height ||
        outputBackgroundColor !== '#ffffff' ||
        canvasAnchorX !== 0.5 ||
        canvasAnchorY !== 0.5)
  );

  const isWatermarkModified = editor.watermarkItems.some(item =>
    item.text?.trim()
  );
  const isQrCodeModified = editor.qrCodeItems.length > 0;

  const isFormatModified = formatId !== 'png';
  const isScaleModified = outputScaleRatio !== 1 || targetOutputPixels != null;
  const isCompressModified = compressionRatio !== 1;

  const handleResetAdjustments = () => {
    editor.setBrightness(0);
    editor.setContrast(0);
    editor.setSaturation(0);
  };

  const handleResetCanvas = () => {
    setCanvasSizeMode('sameAsImage');
    setCanvasAspectRatioId(null);
    setOutputBackgroundColor('#ffffff');
    setCanvasAnchor({ x: 0.5, y: 0.5 });
  };

  const handleResetFormat = () => {
    setFormatId('png');
  };

  const handleResetScale = () => {
    clearTargetOutputPixels();
    setOutputScaleRatio(1);
  };

  /** 手動輸入畫素：失焦時套用；鎖定時依「畫布」長寬比算另一邊 */
  const applyScalePixelsWidthBlur = () => {
    const w = Math.floor(Number(scalePixelWidth.replace(/\D/g, '')) || 0);
    /** 鎖定時用畫布尺寸當比例基準（原圖大小或自訂畫布寬高） */
    const canvasW = effectiveOutputWidth;
    const canvasH = effectiveOutputHeight;
    const ratioSource =
      scalePixelLock && canvasW > 0 && canvasH > 0
        ? { width: canvasW, height: canvasH }
        : null;
    let h: number;
    if (ratioSource) {
      const ratio = ratioSource.width / ratioSource.height;
      h = Math.round(w / ratio);
      h = Math.min(maxPx, Math.max(minPx, h));
    } else {
      h = Math.floor(Number(scalePixelHeight.replace(/\D/g, '')) || 0);
    }
    if (w >= minPx && h >= minPx) {
      setTargetOutputPixels(w, h);
    }
  };

  const applyScalePixelsHeightBlur = () => {
    const h = Math.floor(Number(scalePixelHeight.replace(/\D/g, '')) || 0);
    const canvasW = effectiveOutputWidth;
    const canvasH = effectiveOutputHeight;
    const ratioSource =
      scalePixelLock && canvasW > 0 && canvasH > 0
        ? { width: canvasW, height: canvasH }
        : null;
    let w: number;
    if (ratioSource) {
      const ratio = ratioSource.width / ratioSource.height;
      w = Math.round(h * ratio);
      w = Math.min(maxPx, Math.max(minPx, w));
    } else {
      w = Math.floor(Number(scalePixelWidth.replace(/\D/g, '')) || 0);
    }
    if (w >= minPx && h >= minPx) {
      setTargetOutputPixels(w, h);
    }
  };

  /** 手動輸入畫素模式：從手動尺寸或預估輸出同步到本地輸入框 */
  useEffect(() => {
    if (scaleInputMode !== 'pixels') return;
    const source = targetOutputPixels ?? estimatedOutputSize;
    if (source?.width && source?.height) {
      setScalePixelWidth(String(source.width));
      setScalePixelHeight(String(source.height));
    }
  }, [
    scaleInputMode,
    targetOutputPixels?.width,
    targetOutputPixels?.height,
    estimatedOutputSize?.width,
    estimatedOutputSize?.height,
  ]);

  const handleResetCompression = () => {
    setCompressionRatio(1);
  };

  /** 預覽與輸出一致：預覽畫布為 estimatedOutputSize；圖層在邏輯畫布上 1:1 或縮小，再依輸出比例縮放 */
  const previewOutputWidth = estimatedOutputSize?.width ?? effectiveOutputWidth;
  const previewOutputHeight =
    estimatedOutputSize?.height ?? effectiveOutputHeight;

  /** 圖示／塗鴉／QR／文字上限：依輸出畫布（輸入圖）像素；下限依畫布短邊 2% 或常數 */
  const canvasW = previewOutputWidth || 0;
  const canvasH = previewOutputHeight || 0;
  const canvasShortSide = Math.min(canvasW, canvasH);
  const iconQrSizeMax = getIconQrSizeMax(canvasW, canvasH);
  const watermarkFontSizeMax = getWatermarkFontSizeMax(canvasW, canvasH);
  const iconSizeMin =
    canvasShortSide > 0
      ? Math.max(ICON_QR_SIZE_MIN, Math.round(canvasShortSide * 0.02))
      : ICON_QR_SIZE_MIN;
  const iconSizeMax = Math.max(iconSizeMin, iconQrSizeMax);

  const previewContentSize =
    minOutputCanvasSize &&
    estimatedOutputSize?.width &&
    estimatedOutputSize?.height &&
    effectiveOutputWidth > 0 &&
    effectiveOutputHeight > 0
      ? (() => {
          const min = minOutputCanvasSize;
          const out = estimatedOutputSize!;
          const cw = effectiveOutputWidth;
          const ch = effectiveOutputHeight;
          const logical = getCanvasLogicalContentSize(
            min.width,
            min.height,
            cw,
            ch
          );
          const scale = Math.min(out.width / cw, out.height / ch);
          return {
            width: Math.max(1, Math.round(logical.width * scale)),
            height: Math.max(1, Math.round(logical.height * scale)),
          };
        })()
      : null;

  /** 預覽時疊加層與邏輯畫布→預覽輸出尺寸一致縮放（非「圖 fit 畫布」的錯誤倍率） */
  const previewOverlayScale =
    effectiveOutputWidth > 0 &&
    effectiveOutputHeight > 0 &&
    estimatedOutputSize?.width &&
    estimatedOutputSize?.height
      ? Math.min(
          estimatedOutputSize.width / effectiveOutputWidth,
          estimatedOutputSize.height / effectiveOutputHeight
        )
      : 1;

  const previewIconItems = editor.iconItems.map(item => ({
    ...item,
    size: Math.max(1, Math.round(item.size * previewOverlayScale)),
  }));
  const previewWatermarkItems = editor.watermarkItems.map(item => ({
    ...item,
    fontSize: Math.max(1, Math.round(item.fontSize * previewOverlayScale)),
  }));
  const previewQrCodeItems = editor.qrCodeItems.map(item => ({
    ...item,
    size: Math.max(1, Math.round((item.size ?? 80) * previewOverlayScale)),
  }));

  return (
    <>
      <div className="mb-6 flex w-full min-w-0 flex-col gap-6">
        <div
          className={`min-w-0 rounded-lg border-2 bg-white p-6 transition-colors dark:bg-gray-800 ${
            isDragging
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-gray-200 dark:border-gray-700'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <ImageUp className="h-4 w-4" />
            <span>選擇圖片</span>
          </label>
          <div className="mb-4 flex w-full max-w-full min-w-0 flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 text-center dark:border-gray-600">
            <div className="flex w-full max-w-full flex-wrap items-center justify-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-primary/90">
                <Upload className="h-4 w-4" />
                <span>選擇檔案</span>
                <input
                  type="file"
                  accept={acceptImage}
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  aria-label="選擇圖片（可多選）"
                />
              </label>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                或拖曳圖片到這裡（可多選）
              </span>
            </div>
            {file && (
              <div className="mt-3 w-full min-w-0 text-sm text-gray-600 dark:text-gray-400">
                {editor.files.length > 1 ? (
                  <div className="w-full min-w-0 space-y-2">
                    <p className="font-medium">
                      共 {editor.files.length} 張，批次輸出將打包成 ZIP
                    </p>
                    <div className="flex w-full max-w-full min-w-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => scrollCarousel('prev')}
                        className="shrink-0 rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                        aria-label="上一張"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <div
                          ref={carouselRef}
                          className="flex gap-2 overflow-x-auto overflow-y-hidden py-1 scroll-smooth"
                          style={{
                            scrollSnapType: 'x mandatory',
                            scrollPaddingInline: '4px',
                          }}
                        >
                          {editor.files.map((f, i) => (
                            <div
                              key={`${f.name}-${i}`}
                              className="relative shrink-0"
                              style={{
                                scrollSnapAlign: 'start',
                                width: THUMB_SIZE,
                                height: THUMB_SIZE,
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => editor.setCurrentFileIndex(i)}
                                className={`block h-full w-full overflow-hidden rounded-lg border-2 bg-gray-100 dark:bg-gray-700 ${
                                  editor.currentFileIndex === i
                                    ? 'border-primary ring-2 ring-primary/30'
                                    : 'border-transparent hover:border-gray-400 dark:hover:border-gray-500'
                                }`}
                              >
                                {thumbnailUrls[i] ? (
                                  <img
                                    src={thumbnailUrls[i]}
                                    alt=""
                                    className="h-full w-full object-cover"
                                    width={THUMB_SIZE}
                                    height={THUMB_SIZE}
                                  />
                                ) : (
                                  <span className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                                    {i + 1}
                                  </span>
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => editor.removeFileAt(i)}
                                className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white shadow hover:bg-red-600"
                                aria-label={`刪除第 ${i + 1} 張圖片`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => scrollCarousel('next')}
                        className="shrink-0 rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                        aria-label="下一張"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                    <span className="text-gray-500 dark:text-gray-500">
                      目前編輯：第 {editor.currentFileIndex + 1} 張
                      {originalSize &&
                        `（原圖 ${originalSize.width} × ${originalSize.height} px）`}
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2">
                    <div
                      className="relative overflow-hidden rounded-lg border-2 border-primary bg-gray-100 dark:bg-gray-700 ring-2 ring-primary/30"
                      style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
                    >
                      {thumbnailUrls[0] ? (
                        <img
                          src={thumbnailUrls[0]}
                          alt=""
                          className="h-full w-full object-cover"
                          width={THUMB_SIZE}
                          height={THUMB_SIZE}
                        />
                      ) : null}
                    </div>
                    <span className="text-gray-500 dark:text-gray-500">
                      {originalSize &&
                        `原圖 ${originalSize.width} × ${originalSize.height} px`}
                    </span>
                    <button
                      type="button"
                      onClick={() => editor.removeFileAt(0)}
                      className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                      aria-label="刪除此圖片"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded border border-gray-200 bg-gray-50/50 p-3 dark:border-gray-600 dark:bg-gray-800/50">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Eye className="h-4 w-4" />
                  <span>預覽</span>
                </span>
                {minOutputCanvasSize && (
                  <span className="text-sm tabular-nums text-gray-500 dark:text-gray-400">
                    目前圖片：{minOutputCanvasSize.width} ×{' '}
                    {minOutputCanvasSize.height} px
                  </span>
                )}
              </div>
              <div className="mt-2">
                <CanvasStage
                  imageUrl={effectivePreviewUrl}
                  backgroundColor={outputBackgroundColor || '#ffffff'}
                  backgroundImageUrl={
                    outputBackgroundType === 'image'
                      ? outputBackgroundImage
                      : null
                  }
                  anchorX={canvasAnchorX}
                  anchorY={canvasAnchorY}
                  outputCanvasWidth={previewOutputWidth || effectiveOutputWidth}
                  outputCanvasHeight={
                    previewOutputHeight || effectiveOutputHeight
                  }
                  canvasContentWidth={
                    previewContentSize?.width ?? canvasContentSize?.width ?? 0
                  }
                  canvasContentHeight={
                    previewContentSize?.height ?? canvasContentSize?.height ?? 0
                  }
                  brightness={brightness}
                  contrast={contrast}
                  saturation={saturation}
                  borderShape={borderShape}
                  borderCornerRadius={borderCornerRadius}
                  borderWidth={borderWidth}
                  borderColor={editor.borderColor}
                  iconItems={previewIconItems}
                  onIconAnchorChange={(id, anchor) =>
                    editor.updateIconItem(id, {
                      anchorX: anchor.x,
                      anchorY: anchor.y,
                    })
                  }
                  onIconDoubleClick={(_id, source) =>
                    openFeaturePanel(source === 'doodle' ? 'doodle' : 'icon')
                  }
                  watermarkItems={previewWatermarkItems}
                  onChangeAnchor={setCanvasAnchor}
                  onWatermarkAnchorChange={(id, anchor) =>
                    editor.updateWatermarkItem(id, {
                      anchorX: anchor.x,
                      anchorY: anchor.y,
                    })
                  }
                  onWatermarkClick={handleWatermarkPreviewClick}
                  qrCodeItems={previewQrCodeItems}
                  qrCodePreviewUrls={editor.qrCodePreviewUrls}
                  onQrCodeAnchorChange={(id, anchor) =>
                    editor.updateQrCodeItem(id, {
                      anchorX: anchor.x,
                      anchorY: anchor.y,
                    })
                  }
                  onQrCodeDoubleClick={() => openFeaturePanel('qrcode')}
                />
              </div>
            </div>

            <div
              className="flex flex-wrap justify-center gap-2"
              role="tablist"
              aria-label="功能選項"
            >
              <FeatureToggleButton
                label="去背"
                icon={<Wand2 className="h-4 w-4" />}
                isActive={activeFeatureId === 'removeBg'}
                isModified={Boolean(noBackgroundUrl)}
                onClick={() => openFeaturePanel('removeBg')}
                onReset={() => handleClearNoBackground()}
                tabId="removeBg"
                isTabSelected={featurePanelOpen === 'removeBg'}
              />
              <FeatureToggleButton
                label="裁切"
                icon={<Scissors className="h-4 w-4" />}
                isActive={activeFeatureId === 'crop'}
                isModified={isCropModified}
                onClick={() => openFeaturePanel('crop')}
                onReset={() => handleResetCrop()}
                tabId="crop"
                isTabSelected={featurePanelOpen === 'crop'}
              />
              <FeatureToggleButton
                label="AI 放大"
                icon={<ScanSearch className="h-4 w-4" />}
                isActive={activeFeatureId === 'upscale'}
                isModified={Boolean(upscaledUrl)}
                onClick={() => openFeaturePanel('upscale')}
                onReset={() => handleClearUpscale()}
                tabId="upscale"
                isTabSelected={featurePanelOpen === 'upscale'}
              />
              <FeatureToggleButton
                label="色彩調整"
                icon={<Palette className="h-4 w-4" />}
                isActive={activeFeatureId === 'adjust'}
                isModified={isAdjustModified}
                onClick={() => openFeaturePanel('adjust')}
                onReset={handleResetAdjustments}
                tabId="adjust"
                isTabSelected={featurePanelOpen === 'adjust'}
              />
              <FeatureToggleButton
                label="邊線處理"
                icon={<Square className="h-4 w-4" />}
                isActive={activeFeatureId === 'border'}
                isModified={isBorderModified}
                onClick={() => openFeaturePanel('border')}
                onReset={handleResetBorder}
                tabId="border"
                isTabSelected={featurePanelOpen === 'border'}
              />
              <FeatureToggleButton
                label="畫布尺寸"
                icon={<RectangleHorizontal className="h-4 w-4" />}
                isActive={activeFeatureId === 'canvas'}
                isModified={isCanvasModified}
                onClick={() => openFeaturePanel('canvas')}
                onReset={handleResetCanvas}
                tabId="canvas"
                isTabSelected={featurePanelOpen === 'canvas'}
              />
              <FeatureToggleButton
                label="圖示"
                icon={<ImageIcon className="h-4 w-4" />}
                isActive={activeFeatureId === 'icon'}
                isModified={editor.iconItems.some(i => i.source !== 'doodle')}
                onClick={() => openFeaturePanel('icon')}
                onReset={editor.handleResetIcons}
                tabId="icon"
                isTabSelected={featurePanelOpen === 'icon'}
              />
              <FeatureToggleButton
                key="doodle"
                label="塗鴉"
                icon={<PenTool className="h-4 w-4" />}
                isActive={activeFeatureId === 'doodle'}
                isModified={editor.iconItems.some(i => i.source === 'doodle')}
                onClick={() => openFeaturePanel('doodle')}
                onReset={() => {
                  editor.iconItems
                    .filter(i => i.source === 'doodle')
                    .forEach(i => editor.removeIconItem(i.id));
                }}
                tabId="doodle"
                isTabSelected={featurePanelOpen === 'doodle'}
              />
              <FeatureToggleButton
                label="文字／浮水印"
                icon={<Type className="h-4 w-4" />}
                isActive={activeFeatureId === 'watermark'}
                isModified={isWatermarkModified}
                onClick={() => openFeaturePanel('watermark')}
                onReset={handleResetWatermark}
                tabId="watermark"
                isTabSelected={featurePanelOpen === 'watermark'}
              />
              <FeatureToggleButton
                label="QR Code"
                icon={<QrCode className="h-4 w-4" />}
                isActive={activeFeatureId === 'qrcode'}
                isModified={isQrCodeModified}
                onClick={() => openFeaturePanel('qrcode')}
                onReset={editor.handleResetQrCode}
                tabId="qrcode"
                isTabSelected={featurePanelOpen === 'qrcode'}
              />
              <FeatureToggleButton
                label="輸出格式"
                icon={<FileType className="h-4 w-4" />}
                isActive={activeFeatureId === 'format'}
                isModified={isFormatModified}
                onClick={() => openFeaturePanel('format')}
                onReset={handleResetFormat}
                tabId="format"
                isTabSelected={featurePanelOpen === 'format'}
              />
              <FeatureToggleButton
                label="輸出尺寸比例"
                icon={<Scaling className="h-4 w-4" />}
                isActive={activeFeatureId === 'scale'}
                isModified={isScaleModified}
                onClick={() => openFeaturePanel('scale')}
                onReset={handleResetScale}
                tabId="scale"
                isTabSelected={featurePanelOpen === 'scale'}
              />
              <FeatureToggleButton
                label="壓縮比例"
                icon={<GaugeCircle className="h-4 w-4" />}
                isActive={activeFeatureId === 'compress'}
                isModified={isCompressModified}
                onClick={() => openFeaturePanel('compress')}
                onReset={handleResetCompression}
                tabId="compress"
                isTabSelected={featurePanelOpen === 'compress'}
              />
            </div>

            {featurePanelOpen !== null && (
              <div
                className="rounded-lg border border-gray-200 bg-gray-50/50 dark:border-gray-600 dark:bg-gray-800/50"
                role="tabpanel"
                aria-labelledby={`tab-${featurePanelOpen}`}
              >
                <div className="border-b border-gray-200 px-4 py-2 dark:border-gray-600">
                  <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {featurePanelOpen === 'crop'
                      ? '裁切'
                      : featurePanelOpen === 'removeBg'
                        ? '去背'
                        : featurePanelOpen === 'upscale'
                          ? 'AI 放大'
                          : featurePanelOpen === 'adjust'
                            ? '色彩調整'
                            : featurePanelOpen === 'border'
                              ? '邊線處理'
                              : featurePanelOpen === 'canvas'
                                ? '畫布尺寸'
                                : featurePanelOpen === 'icon'
                                  ? '圖示'
                                  : featurePanelOpen === 'doodle'
                                    ? '塗鴉'
                                    : featurePanelOpen === 'watermark'
                                      ? '文字／浮水印'
                                      : featurePanelOpen === 'qrcode'
                                        ? 'QR Code'
                                        : featurePanelOpen === 'format'
                                          ? '輸出格式'
                                          : featurePanelOpen === 'scale'
                                            ? '輸出尺寸比例'
                                            : '壓縮比例（輸出品質）'}
                  </h3>
                </div>
                <div className="p-4">
                  {featurePanelOpen === 'crop' && (
                    <CropperPanel
                      editor={editor}
                      cropSizeMode={cropSizeMode}
                      setCropSizeMode={handleCropSizeModeChange}
                      embeddedInPanel
                    />
                  )}
                  {featurePanelOpen === 'removeBg' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        一鍵移除圖片背景，僅在裝置內處理。
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveBackground()}
                          disabled={
                            !effectivePreviewUrl || removeBackgroundLoading
                          }
                          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90 disabled:opacity-50"
                        >
                          <Eraser className="h-4 w-4" />
                          {removeBackgroundLoading ? '去背中…' : '執行去背'}
                        </button>
                        {noBackgroundUrl && (
                          <button
                            type="button"
                            onClick={() => handleClearNoBackground()}
                            className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
                          >
                            還原原圖
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {featurePanelOpen === 'upscale' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        只能執行一次，不論 2× 或 4×。
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleUpscale(2)}
                          disabled={!effectivePreviewUrl || upscaleLoading}
                          className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90 disabled:opacity-50"
                        >
                          {upscaleLoading ? '放大中…' : '2×'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpscale(4)}
                          disabled={!effectivePreviewUrl || upscaleLoading}
                          className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90 disabled:opacity-50"
                        >
                          4×
                        </button>
                        {upscaledUrl && (
                          <button
                            type="button"
                            onClick={() => handleClearUpscale()}
                            className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
                          >
                            還原
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {featurePanelOpen === 'adjust' && (
                    <AdjustmentPanel editor={editor} />
                  )}
                  {featurePanelOpen === 'border' && (
                    <BorderPanel editor={editor} />
                  )}
                  {featurePanelOpen === 'icon' && (
                    <IconPanel
                      editor={editor}
                      sizeMin={iconSizeMin}
                      sizeMax={iconSizeMax}
                    />
                  )}
                  {featurePanelOpen === 'doodle' && (
                    <DoodlePanel
                      editor={editor}
                      sizeMin={iconSizeMin}
                      sizeMax={iconSizeMax}
                    />
                  )}
                  {featurePanelOpen === 'watermark' && (
                    <WatermarkPanel
                      editor={editor}
                      expandBlockId={watermarkExpandBlockId}
                      maxFontSize={watermarkFontSizeMax}
                    />
                  )}
                  {featurePanelOpen === 'qrcode' && (
                    <QRCodePanel editor={editor} sizeMax={iconQrSizeMax} />
                  )}
                  {featurePanelOpen === 'canvas' && (
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-3">
                        <label className="flex cursor-pointer items-center gap-2">
                          <input
                            type="radio"
                            name="canvasSizeMode"
                            checked={canvasSizeMode === 'sameAsImage'}
                            onChange={() => {
                              setCanvasSizeMode('sameAsImage');
                              setCanvasAspectRatioId(null);
                            }}
                            className="rounded-full border-gray-300 text-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            原圖大小
                          </span>
                        </label>
                        <label className="flex cursor-pointer items-center gap-2">
                          <input
                            type="radio"
                            name="canvasSizeMode"
                            checked={canvasSizeMode === 'custom'}
                            onChange={() => setCanvasSizeMode('custom')}
                            className="rounded-full border-gray-300 text-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            自訂大小
                          </span>
                        </label>
                      </div>
                      {canvasSizeMode === 'sameAsImage' &&
                        minOutputCanvasSize && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            目前圖片：{minOutputCanvasSize.width} ×{' '}
                            {minOutputCanvasSize.height} px
                          </p>
                        )}
                      {canvasSizeMode === 'custom' && (
                        <>
                          <div className="flex flex-wrap items-center gap-3">
                            <label className="flex items-center gap-2">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                寬
                              </span>
                              <input
                                type="number"
                                min={minOutputCanvasSize?.width ?? minPx}
                                max={maxPx}
                                placeholder="自動"
                                value={outputWidth}
                                onChange={event => {
                                  setOutputWidth(
                                    event.target.value
                                      .replace(/\D/g, '')
                                      .slice(0, 5)
                                  );
                                  setCanvasAspectRatioId(null);
                                }}
                                onBlur={() => {
                                  const minW =
                                    minOutputCanvasSize?.width ?? minPx;
                                  const num = Math.floor(
                                    Number(outputWidth.replace(/\D/g, '')) || 0
                                  );
                                  if (num > 0 && num < minW)
                                    setOutputWidth(String(minW));
                                  commitOutputSize();
                                }}
                                className="w-24 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                aria-label="輸出寬度 px"
                              />
                              <span className="text-sm text-gray-500">px</span>
                            </label>
                            <span className="text-gray-400">×</span>
                            <label className="flex items-center gap-2">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                高
                              </span>
                              <input
                                type="number"
                                min={minOutputCanvasSize?.height ?? minPx}
                                max={maxPx}
                                placeholder="自動"
                                value={outputHeight}
                                onChange={event => {
                                  setOutputHeight(
                                    event.target.value
                                      .replace(/\D/g, '')
                                      .slice(0, 5)
                                  );
                                  setCanvasAspectRatioId(null);
                                }}
                                onBlur={() => {
                                  const minH =
                                    minOutputCanvasSize?.height ?? minPx;
                                  const num = Math.floor(
                                    Number(outputHeight.replace(/\D/g, '')) || 0
                                  );
                                  if (num > 0 && num < minH)
                                    setOutputHeight(String(minH));
                                  commitOutputSize();
                                }}
                                className="w-24 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                aria-label="輸出高度 px"
                              />
                              <span className="text-sm text-gray-500">px</span>
                            </label>
                            <div className="flex flex-wrap items-center gap-3">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                背景
                              </span>
                              <label className="flex cursor-pointer items-center gap-2">
                                <input
                                  type="radio"
                                  name="outputBackgroundType"
                                  checked={outputBackgroundType === 'color'}
                                  onChange={() => {
                                    setOutputBackgroundType('color');
                                    setOutputBackgroundImage(null);
                                  }}
                                  className="rounded-full border-gray-300 text-primary"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  純色
                                </span>
                              </label>
                              <label className="flex cursor-pointer items-center gap-2">
                                <input
                                  type="radio"
                                  name="outputBackgroundType"
                                  checked={outputBackgroundType === 'image'}
                                  onChange={() =>
                                    setOutputBackgroundType('image')
                                  }
                                  className="rounded-full border-gray-300 text-primary"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  圖片
                                </span>
                              </label>
                              {outputBackgroundType === 'color' && (
                                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                  <input
                                    type="color"
                                    value={outputBackgroundColor}
                                    onChange={e =>
                                      setOutputBackgroundColor(e.target.value)
                                    }
                                    className="h-7 w-10 cursor-pointer rounded border border-gray-300 bg-transparent p-0"
                                    aria-label="畫布背景色"
                                  />
                                </label>
                              )}
                              {outputBackgroundType === 'image' && (
                                <span className="flex items-center gap-2">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    id="canvas-bg-image"
                                    onChange={e => {
                                      const f = e.target.files?.[0];
                                      if (!f?.type.startsWith('image/')) return;
                                      const r = new FileReader();
                                      r.onload = () => {
                                        const dataUrl = r.result;
                                        if (typeof dataUrl === 'string')
                                          setOutputBackgroundImage(dataUrl);
                                      };
                                      r.readAsDataURL(f);
                                      e.target.value = '';
                                    }}
                                  />
                                  <label
                                    htmlFor="canvas-bg-image"
                                    className="cursor-pointer rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                                  >
                                    選擇圖片
                                  </label>
                                  {outputBackgroundImage && (
                                    <>
                                      <span className="inline-block h-8 w-8 overflow-hidden rounded border border-gray-300 dark:border-gray-600">
                                        <img
                                          src={outputBackgroundImage}
                                          alt=""
                                          className="h-full w-full object-cover"
                                        />
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setOutputBackgroundImage(null)
                                        }
                                        className="text-sm text-gray-500 hover:text-red-600 dark:hover:text-gray-400 dark:hover:text-red-400"
                                      >
                                        清除
                                      </button>
                                    </>
                                  )}
                                </span>
                              )}
                            </div>
                            {((outputBackgroundType === 'color' &&
                              outputBackgroundColor !== '#ffffff') ||
                              (outputBackgroundType === 'image' &&
                                outputBackgroundImage)) &&
                              !noBackgroundUrl &&
                              file != null && (
                                <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">
                                  若圖片已去背卻仍遮住背景：可能是存成
                                  JPEG（不支援透明，透明處會變白），或圖中部分區塊本身為不透明。請上傳
                                  PNG 或使用本工具「去背」重新處理。
                                </p>
                              )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              長寬比
                            </span>
                            {aspectRatios.map(ratio => (
                              <button
                                key={ratio.id}
                                type="button"
                                onClick={() => {
                                  applyCanvasAspectRatio(ratio.id);
                                  setCanvasAspectRatioId(ratio.id);
                                }}
                                disabled={!minOutputCanvasSize}
                                className={`rounded px-2.5 py-1 text-sm disabled:opacity-50 ${
                                  canvasAspectRatioId === ratio.id
                                    ? 'bg-primary text-white hover:bg-primary/90'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                                }`}
                              >
                                {ratio.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {featurePanelOpen === 'format' && (
                    <div className="flex flex-wrap gap-2">
                      {outputFormats.map(format => (
                        <button
                          key={format.id}
                          type="button"
                          onClick={() => setFormatId(format.id)}
                          className={`rounded px-3 py-1.5 text-sm ${
                            formatId === format.id
                              ? 'bg-primary text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          {format.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {featurePanelOpen === 'scale' && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-3">
                        <label className="flex cursor-pointer items-center gap-2">
                          <input
                            type="radio"
                            name="scaleInputMode"
                            checked={scaleInputMode === 'ratio'}
                            onChange={() => {
                              setScaleInputMode('ratio');
                              clearTargetOutputPixels();
                            }}
                            className="rounded-full border-gray-300 text-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            比例
                          </span>
                        </label>
                        <label className="flex cursor-pointer items-center gap-2">
                          <input
                            type="radio"
                            name="scaleInputMode"
                            checked={scaleInputMode === 'pixels'}
                            onChange={() => setScaleInputMode('pixels')}
                            className="rounded-full border-gray-300 text-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            手動輸入畫素
                          </span>
                        </label>
                      </div>
                      {scaleInputMode === 'ratio' && (
                        <>
                          <div className="flex items-center gap-3">
                            <input
                              type="range"
                              min={0.1}
                              max={1}
                              step={0.1}
                              value={outputScaleRatio}
                              onChange={e =>
                                setOutputScaleRatio(Number(e.target.value))
                              }
                              className="w-full max-w-xs accent-primary"
                              aria-label="輸出尺寸比例 0.1 至 1"
                            />
                            <span className="min-w-[3rem] text-sm tabular-nums text-gray-600 dark:text-gray-400">
                              {outputScaleRatio.toFixed(1)}
                            </span>
                          </div>
                          {estimatedOutputSize && (
                            <p className="text-sm tabular-nums text-gray-600 dark:text-gray-400">
                              預估輸出：{estimatedOutputSize.width} ×{' '}
                              {estimatedOutputSize.height} px
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            0.1 為 10% 尺寸、1 為 100%。
                          </p>
                        </>
                      )}
                      {scaleInputMode === 'pixels' && !estimatedOutputSize && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          請先載入圖片。
                        </p>
                      )}
                      {scaleInputMode === 'pixels' && estimatedOutputSize && (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <label className="flex items-center gap-1.5">
                              <input
                                type="number"
                                min={minPx}
                                max={maxPx}
                                value={scalePixelWidth}
                                onChange={e =>
                                  setScalePixelWidth(
                                    e.target.value.replace(/\D/g, '')
                                  )
                                }
                                onBlur={applyScalePixelsWidthBlur}
                                className="w-20 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 tabular-nums dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                aria-label="輸出寬度 px"
                              />
                              <span className="text-sm text-gray-500">×</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => setScalePixelLock(prev => !prev)}
                              className={`flex items-center rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-600 ${
                                scalePixelLock
                                  ? 'text-gray-700 dark:text-gray-200'
                                  : 'text-gray-400 dark:text-gray-500'
                              }`}
                              title={
                                scalePixelLock
                                  ? '長寬比鎖定中，點擊解除'
                                  : '點擊鎖定長寬比'
                              }
                              aria-label={
                                scalePixelLock
                                  ? '長寬比鎖定中，點擊解除'
                                  : '點擊鎖定長寬比'
                              }
                              aria-pressed={scalePixelLock}
                            >
                              <Lock
                                className={`h-4 w-4 ${
                                  scalePixelLock ? '' : 'opacity-50'
                                }`}
                              />
                            </button>
                            <label className="flex items-center gap-1.5">
                              <input
                                type="number"
                                min={minPx}
                                max={maxPx}
                                value={scalePixelHeight}
                                onChange={e =>
                                  setScalePixelHeight(
                                    e.target.value.replace(/\D/g, '')
                                  )
                                }
                                onBlur={applyScalePixelsHeightBlur}
                                className="w-20 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 tabular-nums dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                aria-label="輸出高度 px"
                              />
                              <span className="text-sm text-gray-500">px</span>
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {scalePixelLock
                              ? '鎖定時以畫布長寬比為準，改寬或高會同步另一邊，失焦套用。'
                              : '解除鎖定可輸入任意寬高，失焦套用。'}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  {featurePanelOpen === 'compress' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min={0.1}
                          max={1}
                          step={0.1}
                          value={compressionRatio}
                          onChange={e =>
                            setCompressionRatio(Number(e.target.value))
                          }
                          className="w-full max-w-xs accent-primary"
                          aria-label="壓縮比例 0.1 至 1"
                        />
                        <span className="min-w-[3rem] text-sm tabular-nums text-gray-600 dark:text-gray-400">
                          {compressionRatio.toFixed(1)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        0.1 高壓縮（檔案小）、1 不壓縮；僅影響 JPEG／WebP，PNG
                        不受影響。
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {processing && (
              <div
                className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 dark:bg-primary/10"
                role="status"
                aria-live="polite"
              >
                <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                  流水線進度
                </p>
                <ul className="space-y-1.5 text-sm">
                  {pipelineCompletedSteps.map(step => (
                    <li
                      key={step}
                      className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
                    >
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-green-500 text-[10px] text-white">
                        ✓
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                  {pipelineCurrentStep && (
                    <li className="flex items-center gap-2 font-medium text-primary dark:text-primary">
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <span>{pipelineCurrentStep}</span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            <div className="flex flex-col items-center gap-3">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  輸出方式：
                </span>
                <select
                  value={gridSliceCount === null ? '' : gridSliceCount}
                  onChange={e => {
                    const v = e.target.value;
                    setGridSliceCount(
                      v === ''
                        ? null
                        : v === 'merge'
                          ? 'merge'
                          : (Number(v) as 9 | 16 | 25)
                    );
                  }}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                  aria-label="輸出方式"
                >
                  <option value="">一般輸出</option>
                  <option value={9}>9 格（3×3）ZIP</option>
                  <option value={16}>16 格（4×4）ZIP</option>
                  <option value={25}>25 格（5×5）ZIP</option>
                  <option value="merge">多圖合併</option>
                </select>
              </div>
              {gridSliceCount === 'merge' && (
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    合併排版：
                  </span>
                  <select
                    value={mergeLayoutId}
                    onChange={e => setMergeLayoutId(e.target.value)}
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                    aria-label="合併排版"
                  >
                    {mergeLayouts.map(opt => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    寬高比：
                  </span>
                  <select
                    value={mergeAspectRatioId}
                    onChange={e => setMergeAspectRatioId(e.target.value)}
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                    aria-label="合併圖寬高比"
                  >
                    {mergeAspectRatios.map(opt => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="button"
                onClick={() =>
                  gridSliceCount === 'merge'
                    ? void handleMergeImages()
                    : gridSliceCount !== null
                      ? void handleNineGridSlice()
                      : void handleOutput({
                          pipelineCropRect:
                            cropRect &&
                            (cropRect.x !== 0 ||
                              cropRect.y !== 0 ||
                              cropRect.width !== 1 ||
                              cropRect.height !== 1)
                              ? cropRect
                              : null,
                        })
                }
                disabled={
                  processing ||
                  (gridSliceCount === 'merge' && editor.files.length < 2)
                }
                className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {processing
                  ? '處理中…'
                  : gridSliceCount === 'merge'
                    ? '輸出並下載（多圖合併）'
                    : gridSliceCount !== null
                      ? `輸出並下載（${gridSliceCount} 格 ZIP）`
                      : '輸出並下載'}
              </button>
            </div>

            {error && (
              <p
                className="mt-2 text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                {error}
              </p>
            )}
          </div>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          按下「輸出並下載」時會依序執行：去背（若開啟）→
          裁切／長寬比（手動裁切框或智能裁切）→ AI 放大（若開啟）→
          色彩調整（亮度／對比／飽和度）→ 邊線處理（圓角／形狀／邊框）→
          畫布尺寸（若自訂）→ 文字／浮水印 → QR Code → 輸出格式與壓縮。
          預覽區可拖曳圖片位置、浮水印與 QR
          位置，所見即所得。多張圖片可多選後一次輸出為 ZIP。網格切割可選
          9／16／25 格，多張圖片可批次切割後一次下載為
          ZIP。所有處理均在裝置內完成，圖片不會上傳至伺服器。若出現錯誤可修正設定後再試。
        </p>
      </section>
    </>
  );
}
